import { HttpResponse } from '@capacitor/core';
import * as Sentry from '@sentry/react';

/**
 * Sentry instrumentation for the Capacitor transport.
 *
 * Requests run through CapacitorHttp, not the global fetch/XHR that Sentry
 * patches, so failed backend calls would otherwise be invisible in replays
 * and in the breadcrumb trail of later error events. These helpers record
 * them explicitly.
 */

/**
 * Breadcrumb category for backend failures.
 *
 * Deliberately NOT 'xhr' or 'fetch': the replay integration drops those from
 * its generic breadcrumb path, because it expects to enrich them from a real
 * XMLHttpRequest or Response object, which CapacitorHttp cannot provide. A
 * custom category passes that filter and reaches the replay timeline.
 */
const BACKEND_CATEGORY = 'backend.error';

/**
 * Strips query params from a request url.
 *
 * Params may carry filter values with member data, and the path alone is
 * enough to identify the endpoint.
 */
const stripQuery = (url: string) => url.split('?')[0];

/**
 * Sentry truncates large breadcrumbs, so keep bodies small enough to survive.
 */
const MAX_BODY_LENGTH = 4096;

const serializeBody = (data: unknown) => {
  if (data === undefined || data === null || data === '') return undefined;

  try {
    const text = typeof data === 'string' ? data : JSON.stringify(data);
    if (text === undefined) return undefined;

    return text.length > MAX_BODY_LENGTH
      ? `${text.slice(0, MAX_BODY_LENGTH)}… (truncated)`
      : text;
  } catch {
    // circular or non serializable payload
    return '[unserializable]';
  }
};

/**
 * Records a failed backend response (4xx, 5xx) as a breadcrumb.
 *
 * Server errors are recorded at error level, client errors at warning level,
 * which is how they appear in the replay timeline.
 */
export const recordFailedResponse = (
  url: string,
  method: string | undefined,
  response: HttpResponse
) => {
  Sentry.addBreadcrumb({
    category: BACKEND_CATEGORY,
    level: response.status >= 500 ? 'error' : 'warning',
    message: `${method ?? 'GET'} ${stripQuery(url)} → ${response.status}`,
    data: {
      url: stripQuery(url),
      method: method ?? 'GET',
      status_code: response.status,
      response_body: serializeBody(response.data)
    }
  });
};

/**
 * Records a transport failure, meaning the request produced no response at
 * all (offline, dns, tls, cancelled).
 */
export const recordTransportError = (
  url: string,
  method: string | undefined,
  error: unknown
) => {
  Sentry.addBreadcrumb({
    category: BACKEND_CATEGORY,
    level: 'error',
    message: `${method ?? 'GET'} ${stripQuery(url)} → no response`,
    data: {
      url: stripQuery(url),
      method: method ?? 'GET',
      error: error instanceof Error ? error.message : String(error)
    }
  });
};
